"use strict";

import { onReady } from '../core/ready';
import Swiper from 'swiper';
import { Navigation, FreeMode } from 'swiper/modules';

const STORAGE_KEY = 'csm-dashboard-sidebar-collapsed';
const COLLAPSED_CLASS = 'is-sidebar-collapsed';

const getStoredState = () => {
    try {
        return window.localStorage ? localStorage.getItem(STORAGE_KEY) : null;
    } catch (error) {
        return null;
    }
};

const setStoredState = (value) => {
    try {
        if (window.localStorage) {
            localStorage.setItem(STORAGE_KEY, value);
        }
    } catch (error) {
        // storage not available, ignore
    }
};

const initDashboardSidebar = () => {
    const page = document.querySelector('.dashboard-page');
    if (!page) return;

    const toggle = page.querySelector('[data-dashboard-toggle]');
    if (!toggle) return;

    const setState = (collapsed) => {
        page.classList.toggle(COLLAPSED_CLASS, collapsed);
        toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        const label = collapsed ? (toggle.dataset.labelCollapsed || '') : (toggle.dataset.labelExpanded || '');
        if (label) {
            toggle.setAttribute('aria-label', label);
        }
    };

    const storedValue = getStoredState();
    const initialCollapsed = storedValue === '1';
    setState(initialCollapsed);

    toggle.addEventListener('click', () => {
        const isCollapsed = page.classList.contains(COLLAPSED_CLASS);
        const nextState = !isCollapsed;
        setState(nextState);
        setStoredState(nextState ? '1' : '0');
    });
};

const initCollectionModal = () => {
    const openBtn = document.querySelector('.btn-create-collection');
    const modal = document.getElementById('dashboardCollectionModal');
    if (!openBtn || !modal) return;

    const form = modal.querySelector('#dashboardCollectionForm');
    const nameInput = modal.querySelector('#collectionName');
    const messageEl = modal.querySelector('.collection-modal__message');
    const fileInput = modal.querySelector('#collectionImage');
    const dropzone = modal.querySelector('[data-upload-dropzone]');
    const previewWrapper = modal.querySelector('[data-upload-preview]');
    const previewImg = modal.querySelector('[data-upload-preview-img]');

    const modalInstance = (window.bootstrap && window.bootstrap.Modal)
        ? window.bootstrap.Modal.getOrCreateInstance(modal)
        : null;

    const resetModalState = () => {
        if (form) form.reset();
        if (messageEl) {
            messageEl.classList.remove('is-error');
            messageEl.textContent = messageEl.dataset.defaultMessage || messageEl.textContent;
        }
        if (dropzone) {
            dropzone.classList.remove('has-file', 'is-hidden');
        }
        if (previewWrapper) {
            previewWrapper.classList.add('d-none');
        }
        if (previewImg) {
            previewImg.removeAttribute('src');
        }
    };

    const show = () => {
        if (modalInstance) {
            modalInstance.show();
        } else {
            modal.style.display = 'block';
            modal.removeAttribute('aria-hidden');
        }
        if (nameInput) nameInput.focus();
    };

    const hide = () => {
        if (modalInstance) {
            modalInstance.hide();
        } else {
            modal.style.display = 'none';
            modal.setAttribute('aria-hidden', 'true');
            resetModalState();
        }
    };

    openBtn.addEventListener('click', (e) => {
        e.preventDefault();
        show();
    });

    modal.addEventListener('hidden.bs.modal', resetModalState);

    const setMessage = (msg, isError = false) => {
        if (!messageEl) return;
        messageEl.textContent = msg;
        messageEl.classList.toggle('is-error', !!isError);
    };

    const submitCollection = async (payload) => {
        const wpUrls = window.WPURLS || {};
        if (!wpUrls.ajaxUrl) throw new Error('Endpoint non disponibile.');

        const formData = new FormData();
        formData.append('action', 'csm_create_collection');
        formData.append('nonce', wpUrls.nonce || '');
        formData.append('collection_name', payload.name);
        if (payload.file) {
            formData.append('collection_image', payload.file, payload.file.name);
        }

        const response = await fetch(wpUrls.ajaxUrl, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin'
        });

        const data = await response.json().catch(() => null);
        if (!data) throw new Error('Risposta non valida dal server.');
        if (!data.success) {
            const msg = data?.data?.message || 'Impossibile creare la collezione.';
            throw new Error(msg);
        }
        return data.data;
    };

    if (form) {
        form.addEventListener('submit', async (event) => {
            event.preventDefault();
            const submitBtn = form.querySelector('#collectionSubmit');
            const file = fileInput && fileInput.files && fileInput.files[0] ? fileInput.files[0] : null;
            const name = nameInput ? nameInput.value.trim() : '';

            if (!name) {
                setMessage('Inserisci un nome per la collezione.', true);
                return;
            }

            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.dataset.loading = '1';
            }

            try {
                const result = await submitCollection({ name, file });
                setMessage(result?.message || 'Collezione creata correttamente.');
                window.dispatchEvent(new CustomEvent('collection:created', { detail: result }));
                setTimeout(() => hide(), 800);
            } catch (error) {
                setMessage(error.message || 'Errore durante la creazione della collezione.', true);
            } finally {
                if (submitBtn) {
                    submitBtn.disabled = false;
                    delete submitBtn.dataset.loading;
                }
            }
        });
    }

    if (fileInput) {
        fileInput.addEventListener('change', () => {
            const file = fileInput.files && fileInput.files[0] ? fileInput.files[0] : null;
            if (dropzone) {
                dropzone.classList.toggle('has-file', !!file);
                dropzone.classList.toggle('is-hidden', !!file);
            }
            if (previewWrapper && previewImg) {
                if (file) {
                    const previewUrl = URL.createObjectURL(file);
                    previewImg.src = previewUrl;
                    previewWrapper.classList.remove('d-none');
                } else {
                    previewImg.removeAttribute('src');
                    previewWrapper.classList.add('d-none');
                }
            }
        });
    }
};

const initCatalogProductSelection = () => {
    const form = document.querySelector('[data-catalog-step=\"products\"]');
    if (!form) return;

    const nextBtn = form.querySelector('[data-catalog-next]');
    const counter = document.querySelector('[data-catalog-selection-counter]');
    const selectedCountEl = counter ? counter.querySelector('[data-catalog-selected-count]') : null;
    const totalCountEl = counter ? counter.querySelector('[data-catalog-total-count]') : null;

    const getCheckboxes = () => form.querySelectorAll('input[type=\"checkbox\"][name=\"catalog_items[]\"]');

    const updateState = () => {
        const selectedCount = form.querySelectorAll('input[type=\"checkbox\"][name=\"catalog_items[]\"]:checked').length;
        const totalCount = getCheckboxes().length;

        if (nextBtn) {
            nextBtn.disabled = selectedCount === 0;
        }

        if (selectedCountEl) {
            selectedCountEl.textContent = String(selectedCount);
        }

        if (totalCountEl) {
            totalCountEl.textContent = String(totalCount);
        }
    };

    form.addEventListener('change', (event) => {
        if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'checkbox') {
            return;
        }
        updateState();
    });
    updateState();
};

const initCatalogPriceCards = () => {
    const form = document.querySelector('[data-catalog-step="prices"]');
    if (!form) return;
    const cards = form.querySelectorAll('.catalog-choice-card');
    const showRadios = form.querySelectorAll('input[name="show_prices"]');

    const syncSelected = () => {
        cards.forEach((card) => card.classList.remove('is-selected'));
        const checked = form.querySelector('input[name="show_prices"]:checked');
        if (checked) {
            const card = checked.closest('.catalog-choice-card');
            if (card) card.classList.add('is-selected');
        }
    };
    showRadios.forEach((radio) => radio.addEventListener('change', syncSelected));
    syncSelected();
};

const initCatalogPriceMode = () => {
    const form = document.querySelector('[data-catalog-step="prices-config"]');
    if (!form) return;

    const markupBlock = form.querySelector('.js-catalog-markup-block');
    const modeRadios = form.querySelectorAll('input[name="price_mode"]');
    const tabLabels = form.querySelectorAll('.catalog-tab');
    const priceCells = form.querySelectorAll('.catalog-price-product-row__price');
    const markupInput = form.querySelector('#markupPercent');
    const markupPriceSpans = form.querySelectorAll('.js-price-markup');

    const update = () => {
        let selectedMode = form.querySelector('input[name="price_mode"]:checked');
        if (!selectedMode) {
            selectedMode = Array.from(modeRadios).find((r) => !r.disabled);
            if (selectedMode) selectedMode.checked = true;
        }
        const mode = selectedMode ? selectedMode.value : 'list';

        if (markupBlock) markupBlock.style.display = mode === 'markup' ? 'flex' : 'none';

        tabLabels.forEach((label) => {
            label.classList.toggle('is-active', label.querySelector('input:checked') !== null);
        });

        priceCells.forEach((cell) => {
            cell.classList.remove('is-mode-list', 'is-mode-markup', 'is-mode-custom');
            cell.classList.add('is-mode-' + mode);
        });
    };

    // Comportamento come catalog-input-pill__input: lo "0" di default scompare al focus
    const clearZeroOnFocus = (input) => {
        if (!input) return;
        input.addEventListener('focus', () => {
            const v = input.value.replace(/,/g, '.');
            if (v === '0' || v === '0.00' || v === '0.0' || parseFloat(v) === 0) {
                input.value = '';
            }
        });
    };
    clearZeroOnFocus(markupInput);
    form.querySelectorAll('.catalog-input-pill__input').forEach(clearZeroOnFocus);

    // Aggiornamento prezzi markup in tempo reale
    const formatPrice = (num) => {
        return num.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    };
    const updateMarkupPrices = () => {
        const pct = parseFloat(markupInput?.value) || 0;
        markupPriceSpans.forEach((span) => {
            const requestText = span.getAttribute('data-request-text') || 'Su richiesta';
            const raw = span.getAttribute('data-list-price');
            if (raw === '' || raw === null) {
                span.textContent = requestText;
                return;
            }
            const listPrice = parseFloat(raw);
            if (Number.isNaN(listPrice)) {
                span.textContent = requestText;
                return;
            }
            const withMarkup = Math.round(listPrice * (1 + pct / 100) * 100) / 100;
            span.textContent = formatPrice(withMarkup);
        });
    };
    if (markupInput) {
        markupInput.addEventListener('input', updateMarkupPrices);
        markupInput.addEventListener('change', updateMarkupPrices);
    }

    modeRadios.forEach((radio) => radio.addEventListener('change', update));
    update();
};

const initCatalogCoverPreview = () => {
    const form = document.querySelector('[data-catalog-step=\"cover\"]');
    if (!form) return;

    const preview = document.querySelector('[data-cover-preview]');
    const previewMain = preview ? preview.querySelector('.catalog-cover-preview__main') : null;
    const titleEl = document.querySelector('[data-cover-preview-title]');
    const titleInput = form.querySelector('#coverTitle');
    const coverTypeInput = form.querySelector('[data-cover-type]');
    const coverBgInput = form.querySelector('[data-cover-bg]');
    const modal = document.querySelector('[data-cover-modal]');
    const modalOpen = document.querySelector('[data-cover-modal-open]');
    const modalClose = document.querySelectorAll('[data-cover-modal-close]');
    const colorsWrap = form.querySelector('.js-cover-colors-wrap');
    const imageTriggerWrap = form.querySelector('.js-cover-image-trigger');
    const typeUiRadios = form.querySelectorAll('input[name="cover_type_ui"]');

    const closeModal = () => {
        if (modal) {
            modal.classList.remove('is-open');
        }
    };

    const setPreview = ({ background = null, textColor = null, isImage = false } = {}) => {
        const target = document.querySelector('.catalog-cover-preview__main') || previewMain || preview;
        if (!target) return;
        if (background) {
            if (isImage) {
                target.style.backgroundImage = `url("${String(background).replace(/"/g, '\\"')}")`;
                target.style.backgroundColor = '';
                target.style.backgroundSize = 'cover';
                target.style.backgroundPosition = 'center';
                target.style.backgroundRepeat = 'no-repeat';
            } else {
                target.style.backgroundImage = '';
                target.style.backgroundSize = '';
                target.style.backgroundPosition = '';
                target.style.backgroundRepeat = '';
                if (background.startsWith('linear-gradient')) {
                    target.style.background = background;
                    target.style.backgroundColor = '';
                } else {
                    target.style.backgroundColor = background;
                    target.style.background = '';
                }
            }
        }
        if (textColor != null && textColor !== '') {
            target.style.color = textColor;
        }
    };

    if (titleInput && titleEl) {
        titleInput.addEventListener('input', () => {
            titleEl.textContent = titleInput.value;
        });
    }

    // Pulsanti Sfondo: Colore vs Fotografico — mostra pallini o apri modale
    const syncCoverTypeUI = () => {
        const checked = form.querySelector('input[name="cover_type_ui"]:checked');
        const value = checked ? checked.value : 'color';
        if (coverTypeInput) coverTypeInput.value = value;
        if (colorsWrap) colorsWrap.style.display = value === 'color' ? '' : 'none';
        if (imageTriggerWrap) imageTriggerWrap.style.display = value === 'image' ? '' : 'none';
        if (value === 'image' && modal) modal.classList.add('is-open');
    };

    typeUiRadios.forEach((radio) => {
        radio.addEventListener('change', syncCoverTypeUI);
        // Se già in modalità Fotografico, click sul pulsante riapre la modale
        const btn = radio.closest('.catalog-cover-type-btn');
        if (btn && radio.value === 'image') {
            btn.addEventListener('click', () => {
                if (radio.checked && modal) modal.classList.add('is-open');
            });
        }
    });

    const colorOptions = form.querySelectorAll('[data-cover-color]');
    colorOptions.forEach((option) => {
        option.addEventListener('change', () => {
            const bg = option.dataset.bg || '';
            const text = option.dataset.text || '';
            if (coverTypeInput) coverTypeInput.value = 'color';
            const valueParts = (option.value || '').split('|');
            if (coverBgInput) coverBgInput.value = valueParts.length >= 2 ? valueParts.slice(0, 2).join('|') : bg;
            setPreview({ background: bg, textColor: text, isImage: false });
        });
    });

    const imageOptions = document.querySelectorAll('[data-cover-image]');
    imageOptions.forEach((option) => {
        option.addEventListener('change', () => {
            const image = option.getAttribute('data-image') || option.dataset.image || '';
            const text = option.getAttribute('data-image-text') || option.dataset.imageText || '';
            if (coverTypeInput) coverTypeInput.value = 'image';
            setPreview({ background: image, textColor: text, isImage: true });
            closeModal();
        });
    });

    const checkedColor = form.querySelector('[data-cover-color]:checked');
    if (checkedColor) {
        const bg = checkedColor.dataset.bg || '';
        const text = checkedColor.dataset.text || '';
        if (coverTypeInput) coverTypeInput.value = 'color';
        const valueParts = (checkedColor.value || '').split('|');
        if (coverBgInput) coverBgInput.value = valueParts.length >= 2 ? valueParts.slice(0, 2).join('|') : bg;
    }

    const checkedImage = document.querySelector('[data-cover-image]:checked');
    if (checkedImage) {
        const image = checkedImage.getAttribute('data-image') || checkedImage.dataset.image || '';
        const text = checkedImage.getAttribute('data-image-text') || checkedImage.dataset.imageText || '';
        if (coverTypeInput) coverTypeInput.value = 'image';
        setPreview({ background: image, textColor: text, isImage: true });
    }

    if (modalOpen && modal) {
        modalOpen.addEventListener('click', (e) => {
            e.preventDefault();
            const radio = form.querySelector('input[name="cover_type_ui"][value="image"]');
            if (radio) {
                radio.checked = true;
                if (coverTypeInput) coverTypeInput.value = 'image';
                if (colorsWrap) colorsWrap.style.display = 'none';
                if (imageTriggerWrap) imageTriggerWrap.style.display = '';
            }
            modal.classList.add('is-open');
        });
    }

    if (modalClose.length) {
        modalClose.forEach((btn) => {
            btn.addEventListener('click', closeModal);
        });
    }

    // Logo: anteprima in preview al caricamento file, toggle Mostra/Nascondi logo
    const logoSection = form.querySelector('[data-cover-logo-section]');
    const logoInput = form.querySelector('[data-cover-logo-input]');
    const logoLabel = form.querySelector('[data-cover-logo-label]');
    const logoRemoveBtn = form.querySelector('[data-cover-logo-remove-btn]');
    const logoRemoveInput = form.querySelector('[data-cover-logo-remove]');
    const logoPreviewBox = form.querySelector('[data-cover-logo-preview]');
    const previewUserLogo = document.querySelector('[data-cover-preview-user-logo]');

    const setUserLogoPreview = (src) => {
        if (!previewUserLogo) return;
        if (src) {
            let img = previewUserLogo.querySelector('.catalog-cover-preview__logo');
            if (!img) {
                img = document.createElement('img');
                img.alt = '';
                img.className = 'catalog-cover-preview__logo';
                previewUserLogo.innerHTML = '';
                previewUserLogo.appendChild(img);
            }
            img.src = src;
            previewUserLogo.querySelector('.catalog-cover-preview__logo-placeholder')?.remove();
        } else {
            previewUserLogo.innerHTML = '<span class="catalog-cover-preview__logo-placeholder" aria-hidden="true"></span>';
        }
    };

    const setLogoState = (hasLogo) => {
        if (logoRemoveBtn) logoRemoveBtn.style.display = hasLogo ? '' : 'none';
        if (logoLabel) logoLabel.style.display = hasLogo ? 'none' : 'inline-flex';
    };

    const clearLogo = () => {
        if (logoInput) logoInput.value = '';
        if (logoRemoveInput) logoRemoveInput.value = '1';
        setUserLogoPreview(null);
        setLogoState(false);
        if (logoPreviewBox) {
            logoPreviewBox.innerHTML = '';
            logoPreviewBox.style.display = 'none';
        }
    };

    if (logoRemoveBtn) {
        logoRemoveBtn.addEventListener('click', () => {
            clearLogo();
        });
    }

    if (logoInput) {
        logoInput.addEventListener('change', () => {
            const file = logoInput.files && logoInput.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
                setUserLogoPreview(reader.result);
                setLogoState(true);
                if (logoRemoveInput) logoRemoveInput.value = '0';
                if (logoPreviewBox) {
                    logoPreviewBox.style.display = '';
                    let img = logoPreviewBox.querySelector('img');
                    if (!img) {
                        img = document.createElement('img');
                        img.alt = '';
                        logoPreviewBox.appendChild(img);
                    }
                    img.src = reader.result;
                }
            };
            reader.readAsDataURL(file);
        });
    }

    if (logoSection && logoRemoveBtn && logoLabel) {
        if (logoPreviewBox && logoPreviewBox.querySelector('img')) {
            setLogoState(true);
        }
    }
};

const initCatalogGenerate = () => {
    const form = document.querySelector('[data-catalog-generate]');
    if (!form) return;

    form.addEventListener('submit', () => {
        const btn = form.querySelector('button[type=\"submit\"]');
        if (btn) {
            btn.disabled = true;
            btn.dataset.loading = '1';
        }
    });
};

const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const loadScript = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
});

const initCatalogPreviewCarousel = () => {
    const carousel = document.querySelector('[data-catalog-preview-carousel]');
    const pdfUrl = carousel?.dataset?.pdfUrl?.trim();
    if (!carousel || !pdfUrl) return;

    const wrap = carousel.closest('.catalog-preview-wrap');
    const track = carousel.querySelector('.catalog-preview-carousel__track');
    const placeholder = carousel.querySelector('[data-catalog-preview-placeholder]');
    const prevBtn = wrap?.querySelector('[data-carousel-prev]');
    const nextBtn = wrap?.querySelector('[data-carousel-next]');
    if (!track || !placeholder) return;

    const setLoading = (loading) => {
        placeholder.classList.toggle('is-loading', loading);
        placeholder.querySelector('span').textContent = loading
            ? (typeof WPURLS !== 'undefined' && WPURLS.catalog_preview_loading) || 'Caricamento anteprima...'
            : (typeof WPURLS !== 'undefined' && WPURLS.catalog_preview_placeholder) || 'Anteprima PDF (carosello pagine)';
        if (prevBtn) prevBtn.disabled = loading;
        if (nextBtn) nextBtn.disabled = loading;
    };

    let pdfDoc = null;
    let swiperInstance = null;

    const renderPage = (pageNum, container, maxWidth) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return Promise.reject(new Error('Canvas 2d not available'));
        container.appendChild(canvas);

        return pdfDoc.getPage(pageNum).then((page) => {
            let scale = 1.5;
            let viewport = page.getViewport({ scale });
            if (maxWidth > 0 && viewport.width > maxWidth) {
                scale = scale * (maxWidth / viewport.width);
                viewport = page.getViewport({ scale });
            }
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            const renderContext = { canvasContext: ctx, viewport };
            return page.render(renderContext).promise;
        });
    };

    const run = () => {
        setLoading(true);
        const pdfjsLib = window.pdfjsLib || window.pdfjs;
        if (!pdfjsLib) {
            loadScript(PDFJS_CDN)
                .then(() => {
                    const lib = window.pdfjsLib || window.pdfjs;
                    if (lib && lib.GlobalWorkerOptions) lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
                    run();
                })
                .catch(() => {
                    setLoading(false);
                    placeholder.querySelector('span').textContent = 'Impossibile caricare l\'anteprima.';
                });
            return;
        }

        if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
        }

        const pdfDocOptions = {
            url: pdfUrl,
            disableFontFace: true,
            standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/',
        };
        pdfjsLib.getDocument(pdfDocOptions).promise
            .then((doc) => {
                pdfDoc = doc;
                const totalPages = doc.numPages;
                if (totalPages === 0) {
                    placeholder.querySelector('span').textContent = 'Nessuna pagina nel PDF.';
                    setLoading(false);
                    return;
                }
                const swiperEl = document.createElement('div');
                swiperEl.className = 'swiper catalog-preview-carousel__swiper';
                swiperEl.setAttribute('aria-hidden', 'true');
                const wrapper = document.createElement('div');
                wrapper.className = 'swiper-wrapper';
                const slideEls = [];
                for (let i = 1; i <= totalPages; i++) {
                    const slide = document.createElement('div');
                    slide.className = 'swiper-slide catalog-preview-carousel__slide';
                    wrapper.appendChild(slide);
                    slideEls.push(slide);
                }
                swiperEl.appendChild(wrapper);
                track.appendChild(swiperEl);

                // Scala canvas per non superare mai la larghezza viewport (evita overflow su mobile)
                const viewportMax = Math.max(280, (window.innerWidth || 320) - 48);
                const trackW = track.clientWidth || 0;
                const maxCanvasWidth = Math.min(trackW > 0 ? trackW : viewportMax, viewportMax);
                const renderPromises = slideEls.map((el, i) => renderPage(i + 1, el, maxCanvasWidth));
                return Promise.all(renderPromises).then(() => {
                    Swiper.use([Navigation, FreeMode]);
                    swiperInstance = new Swiper(swiperEl, {
                        modules: [Navigation, FreeMode],
                        freeMode: true,
                        slidesPerView: 1.2,
                        spaceBetween: 12,
                        breakpoints: {
                            576: {
                                slidesPerView: 1.5,
                                spaceBetween: 14,
                            },
                            768: {
                                slidesPerView: 2.2,
                                spaceBetween: 16,
                            },
                        },
                        navigation: {
                            nextEl: nextBtn,
                            prevEl: prevBtn,
                        },
                    });
                    requestAnimationFrame(() => {
                        swiperInstance.updateSize();
                        swiperInstance.updateSlides();
                        swiperInstance.update();
                        swiperEl.classList.add('is-ready');
                        swiperEl.removeAttribute('aria-hidden');
                        placeholder.classList.add('is-hidden');
                        setLoading(false);
                    });
                });
            })
            .catch((err) => {
                setLoading(false);
                placeholder.classList.remove('is-hidden');
                placeholder.querySelector('span').textContent = 'Errore durante il caricamento del PDF.';
                console.error('Catalog PDF preview:', err);
            });
    };

    run();
};

const QUANTITY_MAX_DEFAULT = 10;

const initRequestQuantitySelector = () => {
    // Delegation su document: funziona anche se il form è dentro accordion o caricato dopo
    document.addEventListener('click', (e) => {
        const minus = e.target.closest('.quantity-minus');
        const plus = e.target.closest('.quantity-plus');
        if (!minus && !plus) return;

        const wrapper = (minus || plus).closest('.quantity-selector');
        if (!wrapper) return;

        const form = wrapper.closest('#dashboardRequestForm');
        if (!form) return;

        e.preventDefault();
        e.stopPropagation();

        const input = wrapper.querySelector('.quantity-input');
        if (!input) return;

        const min = parseInt(input.getAttribute('min'), 10) || 0;
        const max = parseInt(input.getAttribute('max'), 10) || QUANTITY_MAX_DEFAULT;
        let val = parseInt(input.value, 10);
        if (Number.isNaN(val)) val = 0;

        if (minus) {
            val = Math.max(min, val - 1);
        } else {
            val = Math.min(max, val + 1);
        }
        input.value = String(val);
        input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Valore iniziale visibile e coerente per tutti i quantity-input del form
    const form = document.getElementById('dashboardRequestForm');
    if (form) {
        form.querySelectorAll('.quantity-input').forEach((input) => {
            const val = parseInt(input.value, 10);
            if (Number.isNaN(val) || val < 0) {
                input.value = '0';
            } else {
                input.value = String(Math.min(parseInt(input.getAttribute('max'), 10) || QUANTITY_MAX_DEFAULT, val));
            }
        });
    }
};

onReady(() => {
    initDashboardSidebar();
    initCollectionModal();
    initRequestQuantitySelector();
    initCatalogProductSelection();
    initCatalogPriceCards();
    initCatalogPriceMode();
    initCatalogCoverPreview();
    initCatalogGenerate();
    initCatalogPreviewCarousel();

    const reloadCollectionsList = async () => {
        const wpUrls = window.WPURLS || {};
        if (!wpUrls.ajaxUrl) {
            window.location.reload();
            return;
        }
        try {
            const formData = new FormData();
            formData.append('action', 'csm_get_collections_list');
            const res = await fetch(wpUrls.ajaxUrl, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });
            const data = await res.json().catch(() => null);
            if (data?.success && data.data?.html) {
                const container = document.getElementById('collectionsListContainer');
                if (container) {
                    container.innerHTML = data.data.html;
                    setupDeleteModal();
                }
            } else {
                window.location.reload();
            }
        } catch (error) {
            window.location.reload();
        }
    };

    let deleteModalInstance = null;
    let deleteModalEl = null;
    let deleteConfirmBtn = null;
    let deleteCancelBtn = null;
    let pendingDeleteId = null;

    const getDeleteConfirmText = () => {
        if (deleteModalEl && deleteModalEl.dataset.confirmText) {
            return deleteModalEl.dataset.confirmText;
        }
        return 'Vuoi eliminare questa collezione?';
    };

    const resetDeleteModalState = () => {
        pendingDeleteId = null;
        if (deleteConfirmBtn) {
            deleteConfirmBtn.disabled = false;
        }
    };

    const setupDeleteModal = () => {
        deleteModalEl = document.getElementById('deleteCollectionModal');
        if (!deleteModalEl) {
            deleteModalInstance = null;
            deleteConfirmBtn = null;
            deleteCancelBtn = null;
            return;
        }

        deleteConfirmBtn = deleteModalEl.querySelector('[data-delete-confirm]');
        deleteCancelBtn = deleteModalEl.querySelector('[data-delete-cancel]');

        if (window.bootstrap && window.bootstrap.Modal) {
            deleteModalInstance = window.bootstrap.Modal.getOrCreateInstance(deleteModalEl);
        } else {
            deleteModalInstance = null;
        }

        if (deleteCancelBtn && !deleteCancelBtn.dataset.bound) {
            deleteCancelBtn.addEventListener('click', resetDeleteModalState);
            deleteCancelBtn.dataset.bound = '1';
        }

        if (deleteConfirmBtn && !deleteConfirmBtn.dataset.bound) {
            deleteConfirmBtn.addEventListener('click', async () => {
                if (!pendingDeleteId) return;
                deleteConfirmBtn.disabled = true;
                try {
                    await deleteCollection(pendingDeleteId);
                    if (deleteModalInstance) {
                        deleteModalInstance.hide();
                    }
                    await reloadCollectionsList();
                    setupDeleteModal();
                } catch (error) {
                    window.alert(error.message || 'Impossibile eliminare la collezione.');
                } finally {
                    deleteConfirmBtn.disabled = false;
                    resetDeleteModalState();
                }
            });
            deleteConfirmBtn.dataset.bound = '1';
        }

        if (deleteModalEl && !deleteModalEl.dataset.bound) {
            deleteModalEl.addEventListener('hidden.bs.modal', resetDeleteModalState);
            deleteModalEl.dataset.bound = '1';
        }
    };

    setupDeleteModal();

    const deleteCollection = async (collectionId) => {
        const wpUrls = window.WPURLS || {};
        if (!wpUrls.ajaxUrl) {
            throw new Error('Endpoint non disponibile.');
        }
        const formData = new FormData();
        formData.append('action', 'csm_delete_collection');
        formData.append('nonce', wpUrls.nonce || '');
        formData.append('collection_id', collectionId);

        const res = await fetch(wpUrls.ajaxUrl, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin'
        });
        const data = await res.json().catch(() => null);
        if (!data?.success) {
            const message = data?.data?.message || 'Impossibile eliminare la collezione.';
            throw new Error(message);
        }
        return data.data;
    };

    const bindDeleteButtons = () => {
        const container = document.getElementById('collectionsListContainer');
        if (!container) return;

        container.addEventListener('click', async (event) => {
            const target = event.target.closest('.btn-delete-collection');
            if (!target) return;

            event.preventDefault();
            const collectionId = target.dataset.collectionId;
            if (!collectionId) return;

            pendingDeleteId = collectionId;

            if (deleteModalInstance && deleteModalEl) {
                deleteModalInstance.show();
            } else {
                // Fallback se Bootstrap non è disponibile
                const confirmDelete = window.confirm(getDeleteConfirmText());
                if (!confirmDelete) {
                    resetDeleteModalState();
                    return;
                }
                target.disabled = true;
                try {
                    await deleteCollection(collectionId);
                    await reloadCollectionsList();
                } catch (error) {
                    window.alert(error.message || 'Impossibile eliminare la collezione.');
                } finally {
                    target.disabled = false;
                    resetDeleteModalState();
                }
            }
        });
    };

    bindDeleteButtons();
    window.addEventListener('collection:created', reloadCollectionsList);
});
